import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import autoAnimate from '@formkit/auto-animate';
import { toast, Bounce } from 'react-toastify';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Label } from '@/components/ui/label';
import { PhoneIcon, UserIcon, EnvelopeIcon, MapPinIcon, IdentificationIcon, BuildingOfficeIcon } from '@heroicons/react/24/solid';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import Calendar01 from '@/components/calendar-01';
import { flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import EasyCalender from '@/Elements/EasyCalender';
import { Check, Loader, PencilIcon, TrashIcon } from 'lucide-react';
import ConfirmationDialog from './ConfirmationDialog';
import moment from 'moment';
import Arc from '@/lib/Arc';

const ChildrenDataTable = ({ guardian, childrenData, setChildrenData, onSaveCallback, manualSave = false, onSelectedChange = null }) => {
    const [isAddChildDialogOpen, setIsAddChildDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editingChild, setEditingChild] = useState(null);

    const childSchema = Yup.object().shape({
        name: Yup.string().required('Name is required'),
        date_of_birth: Yup.date().required('Date of Birth is required').nullable(),
        gender: Yup.string().required('Gender is required'),
        notes: Yup.string().nullable(),
    });

    useEffect(() => {
        if (manualSave)
            saveChildren();
    }, [childrenData])

    const handleAddChild = (values, { setSubmitting, resetForm }) => {
        const newChild = { ...values, id: String(childrenData.length + 1) };
        setChildrenData(prev => [...prev, newChild]);
        // toast.success('Child added successfully!', { position: 'top-right', autoClose: 3000 });
        setSubmitting(false);
        resetForm();
        setIsAddChildDialogOpen(false);
        // setTimeout(() => saveChildren());
    };

    const handleEditChild = (values, { setSubmitting, resetForm }) => {
        setChildrenData(prev => prev.map(child => (child.id === values.id ? { ...child, ...values } : child)));
        // toast.success('Child updated successfully!', { position: 'top-right', autoClose: 3000 });
        setSubmitting(false);
        resetForm();
        setIsEditDialogOpen(false);
        setEditingChild(null);
        // setTimeout(() => saveChildren());
    };

    const deleteChild = (dismissHandler, childUid) => {
        dismissHandler();
        setSaving(true)
        console.log(childUid);
        // console.log('[ChildrenDataTable] Updated Profile Data:', cleanData);
        Arc().serviceCall('guardian', 'deleteChild', [
            guardian.uid,
            childUid
        ]).then(res => {
            // console.log('[ChildrenDataTable] Updated Profile Data:', cleanData);
            toast.success('Profile saved successfully!');
            setSaving(false);
            setChildrenData(res.result);
            onSaveCallback();
        }).catch(error => {
            console.error('[ChildrenDataTable] Updated Profile Data:', error);
            toast.error('Oops! Failed to save profile updates.');
            setSaving(false);
        });
    };

    const columns = React.useMemo(() => {
        const baseColumns = [
            {
                accessorKey: "name",
                header: "Name",
                minWidth: 150,
                cell: ({ row }) => (
                    <span>{row.original.name}</span>
                ),
            },
            {
                accessorKey: "dob",
                header: "Date of Birth",
                minWidth: 180,
                cell: ({ row }) => (
                    <span>
                        {row.original.dob instanceof Date
                            ? row.original.dob.toLocaleDateString()
                            : moment(row.original.date_of_birth).format("LL")}
                    </span>
                ),
            },
            {
                accessorKey: "gender",
                header: "Gender",
                minWidth: 150,
                cell: ({ row }) => (
                    <span className='uppercase'>{row.original.gender}</span>
                ),
            },
            {
                accessorKey: "notes",
                header: "Notes & Allergies",
                minWidth: 200,
                cell: ({ row }) => (
                    <span>{row.original.notes}</span>
                ),
            },
            {
                id: "actions",
                header: "Actions",
                minWidth: 100,
                cell: ({ row }) => (
                    <div className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => { setEditingChild(row.original); setIsEditDialogOpen(true); }}>
                            <PencilIcon className="h-4 w-4" />
                        </Button>
                        <ConfirmationDialog trigger={(<Button variant="destructive" size="icon" >
                            <TrashIcon className="h-4 w-4" />
                        </Button>)} onConfirm={(dismissHandler) => { deleteChild(dismissHandler, row.original.uid) }} message="Deleting action is unreversable, confirm to delete.">
                        </ConfirmationDialog>
                    </div>
                ),
            },
        ];

        if (onSelectedChange) {
            return [
                {
                    id: "select",
                    header: ({ table }) => (
                        <Checkbox
                            checked={
                                table.getIsAllPageRowsSelected() ||
                                (table.getIsSomePageRowsSelected() && "indeterminate")
                            }
                            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                            aria-label="Select all"
                        />
                    ),
                    cell: ({ row }) => (
                        <Checkbox
                            checked={row.getIsSelected()}
                            onCheckedChange={(value) => row.toggleSelected(!!value)}
                            aria-label="Select row"
                        />
                    ),
                    enableSorting: false,
                    enableHiding: false,
                },
                ...baseColumns,
            ];
        }

        return baseColumns;
    }, [onSelectedChange, childrenData]);

    const [saving, setSaving] = useState(false);


    const [rowSelection, setRowSelection] = React.useState({});

    const table = useReactTable({
        data: childrenData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        state: {
            rowSelection,
        },
    });

    useEffect(() => {
        if (onSelectedChange) {
            const selectedChildren = table.getFilteredSelectedRowModel().rows.map(row => row.original);
            onSelectedChange(selectedChildren);
        }
    }, [rowSelection, onSelectedChange, table]);

    const saveChildren = () => {
        setSaving(true)
        console.log(childrenData);
        const cleanData = childrenData.map(child => ({
            uid: child.uid || null, // Assuming uid might not always be present for new children
            name: child.name,
            date_of_birth: child.date_of_birth instanceof Date
                ? moment(child.date_of_birth).format("YYYY-MM-DD")
                : child.date_of_birth,
            gender: child.gender,
            notes: child.notes || null,
        }));
        console.log(cleanData);
        console.log('[ChildrenDataTable] Updated Profile Data:', cleanData);
        Arc().serviceCall('guardian', 'persistChildren', [
            guardian.uid,
            cleanData
        ]).then(res => {
            console.log('[ChildrenDataTable] Updated Profile Data:', cleanData);
            toast.success('Profile saved successfully!');
            setSaving(false);
            // setGuardian(res.result);
            onSaveCallback();
        }).catch(error => {
            console.error('[ChildrenDataTable] Updated Profile Data:', error);
            toast.error('Oops! Failed to save profile updates.');
            setSaving(false);
        });
    };

    return (
        <div className="w-full">
            <div className="flex items-center py-4 gap-2">
                {/* <h4 className="text-lg font-bold">Children Information</h4> */}

                {manualSave ? (saving ? <Loader className='animate-spin' /> : null) : null}

                <Dialog open={isAddChildDialogOpen} onOpenChange={setIsAddChildDialogOpen}>
                    <DialogTrigger asChild>
                        <Button variant="ghost" className="ml-auto">Add New Child</Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                        <DialogHeader>
                            <DialogTitle>Add New Child</DialogTitle>
                            <DialogDescription>Fill in the details for the new child.</DialogDescription>
                        </DialogHeader>
                        <Formik
                            initialValues={{ name: '', date_of_birth: null, gender: '', notes: '' }}
                            validationSchema={childSchema}
                            onSubmit={handleAddChild}
                        >
                            {({ errors, touched, isSubmitting, handleChange, values, setFieldValue }) => (
                                <Form className="grid gap-4 py-4">
                                    <div className="grid grid-cols-4 items-center gap-4">
                                        <Label htmlFor="child_name" className="text-right">Name</Label>
                                        <Input id="child_name" name="name" value={values.name} onChange={handleChange} className="col-span-3" />
                                    </div>
                                    {errors.name && touched.name && <div className="text-red-500 text-sm col-span-4 text-right">{errors.name}</div>}

                                    <div className="grid grid-cols-4 items-center gap-4">
                                        <Label htmlFor="edit_child_dob" className="text-right">Date of Birth</Label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant={"outline"}
                                                    className="col-span-3 justify-start text-left font-normal"
                                                >
                                                    {values.date_of_birth ? new Date(values.date_of_birth).toLocaleDateString() : "Pick a date"}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0">
                                                <EasyCalender selected={new Date(values.date_of_birth)} onSelect={(date) => setFieldValue('date_of_birth', date)}></EasyCalender>
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    {errors.date_of_birth && touched.date_of_birth && <div className="text-red-500 text-sm col-span-4 text-right">{errors.date_of_birth}</div>}

                                    <div className="grid grid-cols-4 items-center gap-4">
                                        <Label htmlFor="child_gender" className="text-right">Gender</Label>
                                        <Select value={values.gender} onValueChange={(value) => setFieldValue('gender', value)}>
                                            <SelectTrigger className="col-span-3">
                                                <SelectValue placeholder="Select gender" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="male">Male</SelectItem>
                                                <SelectItem value="female">Female</SelectItem>
                                                <SelectItem value="other">Other</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {errors.gender && touched.gender && <div className="text-red-500 text-sm col-span-4 text-right">{errors.gender}</div>}

                                    <div className="grid grid-cols-4 items-center gap-4">
                                        <Label htmlFor="child_notes" className="text-right">Notes / Allergies</Label>
                                        <Textarea id="child_notes" name="notes" value={values.notes} onChange={handleChange} placeholder="Any notes or allergies" className="col-span-3" />
                                    </div>
                                    {errors.notes && touched.notes && <div className="text-red-500 text-sm col-span-4 text-right">{errors.notes}</div>}

                                    <DialogFooter>
                                        <Button type="submit" disabled={isSubmitting}>
                                            Add Child {isSubmitting && <Spinner />}
                                        </Button>
                                    </DialogFooter>
                                </Form>
                            )}
                        </Formik>
                    </DialogContent>
                </Dialog>

                {/* Edit Child Dialog */}
                <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                    <DialogContent className="sm:max-w-[425px]">
                        <DialogHeader>
                            <DialogTitle>Edit Child</DialogTitle>
                            <DialogDescription>Update the details for the child.</DialogDescription>
                        </DialogHeader>
                        {editingChild && (
                            <Formik
                                initialValues={editingChild}
                                validationSchema={childSchema}
                                onSubmit={handleEditChild}
                                enableReinitialize
                            >
                                {({ errors, touched, isSubmitting, handleChange, values, setFieldValue }) => (
                                    <Form className="grid gap-4 py-4">
                                        <div className="grid grid-cols-4 items-center gap-4">
                                            <Label htmlFor="edit_child_name" className="text-right">Name</Label>
                                            <Input id="edit_child_name" name="name" value={values.name} onChange={handleChange} className="col-span-3" />
                                        </div>
                                        {errors.name && touched.name && <div className="text-red-500 text-sm col-span-4 text-right">{errors.name}</div>}

                                        <div className="grid grid-cols-4 items-center gap-4">
                                            <Label htmlFor="edit_child_dob" className="text-right">Date of Birth</Label>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        variant={"outline"}
                                                        className="col-span-3 justify-start text-left font-normal"
                                                    >
                                                        {values.date_of_birth ? new Date(values.date_of_birth).toLocaleDateString() : "Pick a date"}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0">
                                                    <EasyCalender selected={new Date(values.date_of_birth)} onSelect={(date) => setFieldValue('date_of_birth', date)}></EasyCalender>
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                        {errors.date_of_birth && touched.date_of_birth && <div className="text-red-500 text-sm col-span-4 text-right">{errors.date_of_birth}</div>}

                                        <div className="grid grid-cols-4 items-center gap-4">
                                            <Label htmlFor="edit_child_gender" className="text-right">Gender</Label>
                                            <Select value={values.gender} onValueChange={(value) => setFieldValue('gender', value)}>
                                                <SelectTrigger className="col-span-3">
                                                    <SelectValue placeholder="Select gender" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="male">Male</SelectItem>
                                                    <SelectItem value="female">Female</SelectItem>
                                                    <SelectItem value="other">Other</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        {errors.gender && touched.gender && <div className="text-red-500 text-sm col-span-4 text-right">{errors.gender}</div>}

                                        <div className="grid grid-cols-4 items-center gap-4">
                                            <Label htmlFor="edit_child_notes" className="text-right">Notes / Allergies</Label>
                                            <Textarea id="edit_child_notes" name="notes" value={values.notes} onChange={handleChange} placeholder="Any notes or allergies" className="col-span-3" />
                                        </div>
                                        {errors.notes && touched.notes && <div className="text-red-500 text-sm col-span-4 text-right">{errors.notes}</div>}

                                        <DialogFooter>
                                            <Button type="submit" disabled={isSubmitting}>
                                                Save Changes {isSubmitting && <Spinner />}
                                            </Button>
                                        </DialogFooter>
                                    </Form>
                                )}
                            </Formik>
                        )}
                    </DialogContent>
                </Dialog>

                {manualSave ? null : (<Button variant="ghost" type="submit" onClick={saveChildren} disabled={saving}>
                    <Check />
                    Save Details
                    {saving ? <Loader className='animate-spin' /> : null}
                </Button>)}
            </div>
            <div className="overflow-x-auto rounded-md border">
                <Table className="bg-background/80">
                    <TableHeader className="bg-accent/20 text-primary">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead className="text-black dark:text-white border-b-2 border-primary/20 !uppercase" key={header.id} style={{ minWidth: header.column.columnDef.minWidth }}>
                                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id} style={{ minWidth: cell.column.columnDef.minWidth }}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="md:col-span-2">

            </div>
        </div>
    );
};

export default ChildrenDataTable;
